import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableRow,
  alpha,
  type Breakpoint,
  type Theme,
} from '@mui/material';
import {messages} from '@smartmixin/i18n';
import React from 'react';
import {useIntl} from 'react-intl';
import {useScrollOverflow} from './useScrollOverflow';

export type DataTableColumn<Row> = {
  id: string;
  header: React.ReactNode;
  /** What the column shows for a row. */
  cell: (row: Row) => React.ReactNode;
  /** The column's cell in the bold footer row (a total, a mean). */
  footer?: React.ReactNode;
};

export type DataTableProps<Row> = {
  /**
   * The table's caption (visually hidden) and the scroll region's name: the
   * report's title, e.g. "Monthly Outdoor Report, September 2026".
   */
  caption: string;
  /**
   * Names the scroll region apart from the caption: a chart card's table is
   * "Temperature table" inside the card named "Temperature". Default: the
   * caption.
   */
  label?: string;
  /** The first column heads each row (a date); the others hold numbers. */
  columns: DataTableColumn<Row>[];
  rows: readonly Row[];
  rowKey: (row: Row, index: number) => React.Key;
  /**
   * A row that is one statement, not values: its first column as usual and
   * this across all the others (a run of days with no reading at all).
   */
  span?: (row: Row) => React.ReactNode;
  /** A stronger rule under every seventh row: a week of daily rows. */
  weekRule?: boolean;
  /**
   * Scroll vertically past this height, under the sticky header and footer.
   * Responsive like any sx value: `{lg: 'calc(100vh - 120px)'}`.
   */
  maxHeight?: number | string | Partial<Record<Breakpoint, number | string>>;
  'data-testid'?: string;
};

const VISUALLY_HIDDEN = {
  border: 0,
  clip: 'rect(0 0 0 0)',
  height: '1px',
  margin: '-1px',
  overflow: 'hidden',
  padding: 0,
  position: 'absolute',
  whiteSpace: 'nowrap',
  width: '1px',
} as const;

const wash = (colour: string) => `linear-gradient(${colour}, ${colour})`;

/** Data-table density: 28px rows, 40px under a finger or on a phone. */
const TOUCH = '@media (pointer: coarse), (max-width: 599.95px)';

/**
 * The design-system data table (spec section 3, "Data table"): MUI's small
 * table at 28px rows (40 on touch and phones), 8px cell padding, data figures
 * at 13px tabular, the header 12/600 on the page surface, hairline rows with
 * an optional week rule every seventh row, numbers right-aligned, and a bold
 * footer on the page surface. The first column heads its row and stays in
 * place while a narrow screen scrolls the values sideways.
 *
 * The table sits in a region named by its caption. The region scrolls
 * sideways when the table is wider; with `maxHeight` it also scrolls down,
 * and only then do the header and footer stick (inside the region, not to
 * the page). While it scrolls, the region takes focus (a keyboard can then
 * scroll it) and, when that is sideways, says so in its name
 * (`reports.tableScrollRegion`), and a shadow marks the sticky column's edge.
 */
export const DataTable = <Row,>({
  caption,
  label = caption,
  columns,
  rows,
  rowKey,
  span,
  weekRule = false,
  maxHeight,
  'data-testid': testId,
}: DataTableProps<Row>) => {
  const {formatMessage} = useIntl();
  const regionRef = React.useRef<HTMLDivElement>(null);
  const tableRef = React.useRef<HTMLTableElement>(null);
  const overflow = useScrollOverflow(regionRef, tableRef);
  const hasFooter = columns.some((c) => c.footer !== undefined);

  const cell = {
    height: 28,
    [TOUCH]: {height: 40},
    px: 1,
    py: 0,
    '&:first-of-type': {pl: 1.75},
    '&:last-of-type': {pr: 1.75},
    typography: 'data',
    color: 'text.primary',
    borderBottom: 1,
    borderColor: 'divider',
    whiteSpace: 'nowrap',
    textAlign: 'right',
  } as const;

  // The first column: left, in place while the values scroll under it.
  const sticky = {
    position: 'sticky',
    left: 0,
    textAlign: 'left',
    '[data-overflow-x] &': {
      boxShadow: (theme: Theme) =>
        `1px 0 0 ${theme.palette.divider}, 6px 0 8px -6px ${alpha(theme.palette.common.black, 0.35)}`,
    },
  } as const;

  const header = {
    ...cell,
    position: 'sticky',
    top: 0,
    zIndex: 1,
    bgcolor: 'background.default',
    color: 'text.secondary',
    typography: 'caption',
    // The spec's 12/600: there is no theme key for 600 outside `title`.
    fontWeight: 600,
    lineHeight: 1.2,
    whiteSpace: 'normal',
    verticalAlign: 'bottom',
    py: 0.5,
  } as const;

  return (
    <Box
      ref={regionRef}
      role="region"
      aria-label={
        overflow.x
          ? formatMessage(messages.reports.tableScrollRegion, {title: label})
          : label
      }
      tabIndex={overflow.x || overflow.y ? 0 : undefined}
      data-testid={testId}
      data-overflow-x={overflow.x ? '' : undefined}
      sx={{
        maxHeight,
        overflow: 'auto',
        scrollbarWidth: 'thin',
        // Inside the card's clip: an outside ring would be cut off.
        '&&:focus-visible': {outlineOffset: '-2px'},
      }}>
      <Table
        ref={tableRef}
        size="small"
        sx={{
          borderCollapse: 'separate',
          borderSpacing: 0,
          ...(weekRule && {
            '& tbody tr:nth-of-type(7n):not(:last-of-type) > *': {
              borderBottomColor: (theme: Theme) =>
                alpha(theme.palette.border.control, 0.55),
            },
          }),
        }}>
        <Box component="caption" sx={VISUALLY_HIDDEN}>
          {caption}
        </Box>
        <TableHead>
          <TableRow>
            {columns.map((column, index) => (
              <TableCell
                key={column.id}
                sx={[
                  header,
                  index === 0 && {...sticky, zIndex: 2},
                  index > 0 && {textAlign: 'right'},
                ]}>
                {column.header}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, rowIndex) => (
            <TableRow
              key={rowKey(row, rowIndex)}
              sx={{
                // The hover wash is translucent, so it goes over an opaque
                // card fill: the sticky column must hide what scrolls under.
                '& > *': {bgcolor: 'background.paper'},
                '&:hover > *': {
                  backgroundImage: (theme: Theme) =>
                    wash(theme.palette.action.hover),
                },
              }}>
              {span?.(row) != null
                ? [
                    <TableCell
                      key={columns[0].id}
                      component="th"
                      scope="row"
                      sx={[
                        cell,
                        sticky,
                        {color: 'text.secondary', fontWeight: 'inherit'},
                      ]}>
                      {columns[0].cell(row)}
                    </TableCell>,
                    <TableCell
                      key="span"
                      colSpan={columns.length - 1}
                      sx={[
                        cell,
                        {color: 'text.secondary', fontStyle: 'italic'},
                      ]}>
                      {span(row)}
                    </TableCell>,
                  ]
                : columns.map((column, index) =>
                    index === 0 ? (
                      <TableCell
                        key={column.id}
                        component="th"
                        scope="row"
                        sx={[
                          cell,
                          sticky,
                          {color: 'text.secondary', fontWeight: 'inherit'},
                        ]}>
                        {column.cell(row)}
                      </TableCell>
                    ) : (
                      <TableCell key={column.id} sx={cell}>
                        {column.cell(row)}
                      </TableCell>
                    ),
                  )}
            </TableRow>
          ))}
        </TableBody>
        {hasFooter && (
          <TableFooter>
            <TableRow>
              {columns.map((column, index) => (
                <TableCell
                  key={column.id}
                  component={index === 0 ? 'th' : 'td'}
                  scope={index === 0 ? 'row' : undefined}
                  sx={[
                    cell,
                    index === 0 && sticky,
                    {
                      position: 'sticky',
                      bottom: 0,
                      zIndex: index === 0 ? 2 : 1,
                      fontWeight: 'fontWeightBold',
                      color: 'text.primary',
                      bgcolor: 'background.default',
                      borderTop: 1,
                      borderTopColor: 'border.control',
                      borderBottom: 0,
                    },
                  ]}>
                  {column.footer}
                </TableCell>
              ))}
            </TableRow>
          </TableFooter>
        )}
      </Table>
    </Box>
  );
};
